import {
  Button,
  Flex,
  FlexItem,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Label,
  LabelGroup,
  MenuToggle,
  MenuToggleElement,
  Select,
  SelectList,
  SelectOption,
  TextInput,
  Tooltip
} from '@patternfly/react-core';
import { PlusCircleIcon } from '@patternfly/react-icons';
import * as React from 'react';

import {
  RecipientSourceInfo,
  RecipientToken,
  formatRecipientList,
  formatRecipientToken,
  parseRecipientList
} from '@app/model/Communication';

/**
 * What each source is called in the editor, and what it resolves to.
 *
 * Keyed rather than exhaustive, and every lookup falls back to the constant's own name: the
 * catalog is the service's, so a source added there has to appear here — unlabelled but usable —
 * rather than being silently dropped from a picker built from a list this file also holds.
 */
const SOURCE_COPY: Record<string, { label: string; description: string; valueLabel?: string }> = {
  LITERAL: {
    label: 'An address',
    description: 'One address, written out. Prefer a parameter when the same list is used by more than one communication.',
    valueLabel: 'Email address'
  },
  ROLE: {
    label: 'Everyone with a role',
    description: 'Everybody holding one Keycloak role, resolved through the user directory.',
    valueLabel: 'Role name'
  },
  PARAMETER: {
    label: 'A setting’s address list',
    description: 'The addresses held in a list setting, so they can be changed in one place under General Settings.',
    valueLabel: 'Setting name'
  },
  KLASS_TSS: { label: 'The class’s TSS', description: 'The training specialist strategic on the class.' },
  KLASS_FACILITY_CONTACT: { label: 'The class’s facility contact', description: 'The contact address on the class’s facility.' },
  KLASS_CLIENT_EXECUTIVE: { label: 'The class’s client executive', description: 'The client executive on the class.' },
  KLASS_REQUESTED_INSTRUCTORS: { label: 'Requested instructors', description: 'The instructors the class asked for.' },
  KLASS_ALLOCATED_INSTRUCTORS: { label: 'Allocated instructors', description: 'The instructors provisionally assigned to the class.' },
  KLASS_FINAL_INSTRUCTORS: { label: 'Final instructors', description: 'The instructors confirmed to teach the class.' }
};

const labelOf = (name: string) => SOURCE_COPY[name]?.label ?? name;
const describe = (name: string) => SOURCE_COPY[name]?.description ?? '';

export interface RecipientListEditorProps {
  fieldId: string;
  /** TO, CC or BCC — used in the warnings, which are about the line rather than the source. */
  field: 'TO' | 'CC' | 'BCC';
  value: string | null;
  /** What the catalog declares for this line, which is what clearing it restores. */
  defaultValue: string | null;
  sources: RecipientSourceInfo[];
  isDisabled?: boolean;
  onChange(value: string | null): void;
}

/**
 * One recipient line of a communication, as chips rather than as the comma separated text the
 * column actually holds.
 *
 * Chips because a token has two halves that are validated separately — a source the service must
 * recognise, and a value it must need or refuse — and a free text box gets both wrong at once,
 * answered by a 400 with an empty body. Building the token from a picker means the only thing that
 * can be mistyped is the address itself.
 *
 * **Clearing this line does not empty it.** Blank counts as null throughout the service, so an
 * empty list means "no opinion, use the declared recipients" and saving it puts them back. That is
 * deliberate — there is no way to say "emailed, but to nobody", because a configuration that
 * looked purposeful and sent nothing would be worse than either state — so the editor says so out
 * loud rather than letting an operator discover it after saving.
 */
export const RecipientListEditor: React.FunctionComponent<RecipientListEditorProps> = ({
  fieldId,
  field,
  value,
  defaultValue,
  sources,
  isDisabled = false,
  onChange
}) => {
  const tokens = React.useMemo(() => parseRecipientList(value), [value]);

  const [isSourceOpen, setIsSourceOpen] = React.useState(false);
  const [draftSource, setDraftSource] = React.useState<string>('');
  const [draftValue, setDraftValue] = React.useState('');

  const selected = sources.find(source => source.name === draftSource);
  const needsValue = selected?.needsValue ?? false;
  const canAdd = !!selected && (!needsValue || draftValue.trim().length > 0);

  const replace = (next: RecipientToken[]) => onChange(formatRecipientList(next));

  const add = () => {
    if (!selected) return;
    const token: RecipientToken = {
      source: selected.name,
      // An entity derived source takes its value from the notification's target, and the service
      // refuses a token that carries one anyway rather than ignoring it.
      value: needsValue ? draftValue.trim() : null
    };
    // Adding the same recipient twice mails them twice. Silently a no-op rather than an error:
    // the line already says what the operator was asking it to say.
    if (!tokens.some(existing => formatRecipientToken(existing) === formatRecipientToken(token))) {
      replace([...tokens, token]);
    }
    setDraftValue('');
  };

  const remove = (token: RecipientToken) =>
    replace(tokens.filter(existing => formatRecipientToken(existing) !== formatRecipientToken(token)));

  /** A source that resolves to several addresses shows every recipient the others, unless it is
   * on BCC. Said here rather than only in the warnings a save comes back with. */
  const exposed = field === 'BCC'
    ? []
    : tokens.filter(token => sources.find(source => source.name === token.source)?.multiValued
        && token.source !== 'ROLE');

  const declared = parseRecipientList(defaultValue);
  const clearingRestoresDefaults = tokens.length === 0 && declared.length > 0;

  return (
    <div>
      {tokens.length > 0 ? (
        <LabelGroup numLabels={8} aria-label={`${field} recipients`}>
          {tokens.map(token => (
            <Label
              key={formatRecipientToken(token)}
              color={token.source === 'LITERAL' ? 'grey' : 'blue'}
              onClose={isDisabled ? undefined : () => remove(token)}
              closeBtnAriaLabel={`Remove ${formatRecipientToken(token)}`}
            >
              <Tooltip content={describe(token.source) || token.source}>
                <span>{token.value ? `${labelOf(token.source)}: ${token.value}` : labelOf(token.source)}</span>
              </Tooltip>
            </Label>
          ))}
        </LabelGroup>
      ) : (
        <span style={{ color: 'var(--pf-t--global--text--color--subtle)', fontStyle: 'italic' }}>
          nobody
        </span>
      )}

      {!isDisabled && (
        <Flex
          spaceItems={{ default: 'spaceItemsSm' }}
          alignItems={{ default: 'alignItemsCenter' }}
          style={{ marginTop: 'var(--pf-t--global--spacer--sm)' }}
        >
          <FlexItem grow={{ default: 'grow' }}>
            <Select
              isOpen={isSourceOpen}
              selected={draftSource}
              onOpenChange={setIsSourceOpen}
              onSelect={(_event, next) => {
                setDraftSource(next as string);
                setDraftValue('');
                setIsSourceOpen(false);
              }}
              // The id belongs on the toggle: Select puts its props on the menu, which exists
              // only while it is open.
              toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                <MenuToggle
                  id={fieldId}
                  ref={toggleRef}
                  isExpanded={isSourceOpen}
                  onClick={() => setIsSourceOpen(!isSourceOpen)}
                  style={{ width: '100%' }}
                >
                  {draftSource ? labelOf(draftSource) : 'Add a recipient…'}
                </MenuToggle>
              )}
            >
              <SelectList>
                {sources.length === 0 ? (
                  <SelectOption isDisabled key="none" value="">
                    The recipient sources could not be read
                  </SelectOption>
                ) : (
                  sources.map(source => (
                    <SelectOption key={source.name} value={source.name} description={describe(source.name)}>
                      {labelOf(source.name)}
                    </SelectOption>
                  ))
                )}
              </SelectList>
            </Select>
          </FlexItem>

          {needsValue && (
            <FlexItem grow={{ default: 'grow' }}>
              <TextInput
                id={`${fieldId}-value`}
                aria-label={SOURCE_COPY[draftSource]?.valueLabel ?? 'Value'}
                placeholder={SOURCE_COPY[draftSource]?.valueLabel ?? 'Value'}
                value={draftValue}
                onChange={(_event, next) => setDraftValue(next)}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    if (canAdd) add();
                  }
                }}
              />
            </FlexItem>
          )}

          <FlexItem>
            <Button
              variant="secondary"
              icon={<PlusCircleIcon />}
              isDisabled={!canAdd}
              onClick={add}
              aria-label={`Add a ${field} recipient`}
            >
              Add
            </Button>
          </FlexItem>
        </Flex>
      )}

      <FormHelperText>
        <HelperText>
          {clearingRestoresDefaults && (
            <HelperTextItem variant="warning">
              Empty means “no opinion”, not “nobody”: saving this puts the declared recipients back
              ({declared.map(formatRecipientToken).join(', ')}). To stop mailing this communication,
              turn Email off instead.
            </HelperTextItem>
          )}
          {exposed.map(token => (
            <HelperTextItem key={formatRecipientToken(token)} variant="warning">
              {labelOf(token.source)} can resolve to several addresses, and on {field} each recipient
              sees all the others. BCC keeps them apart.
            </HelperTextItem>
          ))}
        </HelperText>
      </FormHelperText>
    </div>
  );
};
